import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
  Loader2, X, Bot, UserSearch, PhoneCall, FileText,
  Play, Sparkles, Clock, AlertTriangle,
  Download, History, ArrowLeft, BarChart3, Calendar, Trash2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUTINES = [
  {
    type: "prequalification",
    title: "Pre-qualification",
    description: "Analyse IA de vos prospects pour scorer et prioriser les leads",
    icon: UserSearch,
    color: "#3b82f6",
    gradient: "from-blue-500/10 to-blue-600/5",
  },
  {
    type: "daily_calls",
    title: "Liste d'appels du jour",
    description: "Generation automatique de votre planning d'appels prioritaires",
    icon: PhoneCall,
    color: "#22c55e",
    gradient: "from-green-500/10 to-green-600/5",
  },
  {
    type: "devis_relance",
    title: "Relances devis",
    description: "Identification et messages de relance pour vos devis en attente",
    icon: FileText,
    color: "#f59e0b",
    gradient: "from-amber-500/10 to-amber-600/5",
  },
];

const ROUTINE_MAP = Object.fromEntries(ROUTINES.map(r => [r.type, r]));

export function CrmRoutinesPanel({ onClose, onRoutineResult }) {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});
  const [activeRoutine, setActiveRoutine] = useState(null);
  const [view, setView] = useState("routines");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [stats, setStats] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const [h, s] = await Promise.all([
        api.get("/ai-agents/crm/routines/history?limit=50"),
        api.get("/ai-agents/crm/routines/stats").catch(() => ({ data: null })),
      ]);
      setHistory(h.data || []);
      setStats(s.data);
    } catch {}
    setHistoryLoading(false);
  };

  useEffect(() => {
    if (view === "history") loadHistory();
  }, [view]);

  const executeRoutine = async (routineType) => {
    if (loading[routineType]) return;
    setLoading(prev => ({ ...prev, [routineType]: true }));
    setActiveRoutine(routineType);
    try {
      const r = await api.post(`/ai-agents/crm/routines/${routineType}`, {});
      setResults(prev => ({
        ...prev,
        [routineType]: { success: true, data: r.data, timestamp: new Date().toISOString() },
      }));
      if (onRoutineResult) onRoutineResult(routineType, r.data);
    } catch (e) {
      setResults(prev => ({
        ...prev,
        [routineType]: { success: false, error: e.response?.data?.detail || "Erreur lors de l'execution", timestamp: new Date().toISOString() },
      }));
    }
    setLoading(prev => ({ ...prev, [routineType]: false }));
  };

  const downloadPdf = (executionId) => {
    const token = localStorage.getItem("blitzz_token");
    const url = `${API}/ai-agents/crm/routines/history/${executionId}/pdf`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error("Erreur PDF");
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `routine_${executionId}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(() => alert("Erreur lors du telechargement du PDF"));
  };

  const deleteExecution = async (executionId) => {
    if (!window.confirm("Supprimer cette execution ?")) return;
    setDeleting(executionId);
    try {
      await api.delete(`/ai-agents/crm/routines/history/${executionId}`);
      setHistory(h => h.filter(x => x.execution_id !== executionId));
      if (historyDetail?.execution_id === executionId) setHistoryDetail(null);
      if (stats) setStats({ ...stats, total: Math.max(0, stats.total - 1) });
    } catch {}
    setDeleting(null);
  };

  const clearAllHistory = async () => {
    if (!window.confirm("Supprimer TOUT l'historique des routines ?")) return;
    try {
      await api.delete("/ai-agents/crm/routines/history");
      setHistory([]);
      setHistoryDetail(null);
      loadHistory();
    } catch {}
  };

  const formatResult = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <div key={i} className="h-2" />;
      if (trimmed.startsWith("###")) return <h4 key={i} className="text-sm font-bold text-white mt-3 mb-1">{trimmed.replace(/^#+\s*/, "")}</h4>;
      if (trimmed.startsWith("##")) return <h3 key={i} className="text-sm font-bold text-[#f7c935] mt-4 mb-1">{trimmed.replace(/^#+\s*/, "")}</h3>;
      if (trimmed.startsWith("#")) return <h2 key={i} className="text-base font-black text-white mt-4 mb-2">{trimmed.replace(/^#+\s*/, "")}</h2>;
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        return (
          <div key={i} className="flex gap-2 py-0.5 pl-2">
            <span className="text-[#f7c935] mt-1 flex-shrink-0">&#8226;</span>
            <span className="text-sm text-slate-300 leading-relaxed">{formatInlineText(trimmed.slice(2))}</span>
          </div>
        );
      }
      if (/^\d+[\.\)]/.test(trimmed)) {
        const num = trimmed.match(/^(\d+[\.\)])\s*(.*)/);
        return (
          <div key={i} className="flex gap-2 py-0.5 pl-2">
            <span className="text-[#f7c935] font-bold text-sm flex-shrink-0 w-6">{num[1]}</span>
            <span className="text-sm text-slate-300 leading-relaxed">{formatInlineText(num[2])}</span>
          </div>
        );
      }
      return <p key={i} className="text-sm text-slate-300 leading-relaxed py-0.5">{formatInlineText(trimmed)}</p>;
    });
  };

  const formatInlineText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return <strong key={i} className="text-white font-semibold">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <div className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden" data-testid="crm-routines-panel">
      {/* Header with tabs */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
        {historyDetail && (
          <button onClick={() => setHistoryDetail(null)} className="p-1 rounded-lg hover:bg-white/10 text-slate-400" data-testid="back-to-routines-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#f7c935]/20">
          <Sparkles className="w-4 h-4 text-[#f7c935]" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-bold text-[#f7c935]">
            {historyDetail ? "Detail execution" : "Routines IA CRM"}
          </div>
        </div>

        {/* Tabs */}
        {!historyDetail && (
          <div className="flex border border-white/10 rounded-lg overflow-hidden">
            <button onClick={() => setView("routines")} data-testid="routines-tab"
              className={`px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ${view === "routines" ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:bg-white/5"}`}>
              <Play className="w-3 h-3" /> Routines
            </button>
            <button onClick={() => setView("history")} data-testid="history-tab"
              className={`px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 ${view === "history" ? "bg-[#f7c935] text-[#14101c]" : "text-slate-400 hover:bg-white/5"}`}>
              <History className="w-3 h-3" /> Historique
            </button>
          </div>
        )}

        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 text-slate-400" data-testid="close-routines-btn">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* ─── Routines View ─── */}
      {view === "routines" && (
        <div className="p-4 space-y-4">
          {/* Routine cards - compact row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ROUTINES.map(routine => {
              const Icon = routine.icon;
              const result = results[routine.type];
              const isLoading = loading[routine.type];

              return (
                <div key={routine.type} className={`rounded-xl border overflow-hidden transition-all cursor-pointer ${activeRoutine === routine.type ? "border-[#f7c935]/40 ring-1 ring-[#f7c935]/20" : "border-white/5 hover:border-white/10"}`}
                  onClick={() => !isLoading && setActiveRoutine(activeRoutine === routine.type ? null : routine.type)}
                  data-testid={`routine-card-${routine.type}`}>
                  <div className={`bg-gradient-to-r ${routine.gradient} p-4`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: routine.color + "20" }}>
                        <Icon className="w-4 h-4" style={{ color: routine.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white">{routine.title}</div>
                        <div className="text-[11px] text-slate-400 leading-tight">{routine.description}</div>
                      </div>
                    </div>
                    <Button
                      onClick={(e) => { e.stopPropagation(); executeRoutine(routine.type); }}
                      disabled={isLoading}
                      className="w-full px-3 py-2 h-auto text-xs font-bold rounded-lg"
                      style={{ background: isLoading ? routine.color + "40" : routine.color, color: "#fff" }}
                      data-testid={`run-routine-${routine.type}`}
                    >
                      {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
                      {isLoading ? "Analyse en cours..." : "Lancer la routine"}
                    </Button>
                    {result && !isLoading && (
                      <div className="flex items-center gap-2 mt-2.5 text-[10px]">
                        {result.success ? (
                          <span className="text-green-400 flex items-center gap-1"><Bot className="w-3 h-3" /> Resultat disponible</span>
                        ) : (
                          <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Erreur</span>
                        )}
                        <span className="text-slate-500 ml-auto">
                          {new Date(result.timestamp).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Full-width result panel below cards */}
          {activeRoutine && results[activeRoutine] && (
            <div className="rounded-xl border border-white/10 bg-[#0b0811] overflow-hidden" data-testid={`result-content-${activeRoutine}`}>
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  {(() => { const r = ROUTINE_MAP[activeRoutine]; const I = r?.icon || Sparkles; return <I className="w-4 h-4" style={{ color: r?.color }} />; })()}
                  <span className="text-sm font-bold text-white">{ROUTINE_MAP[activeRoutine]?.title}</span>
                  {results[activeRoutine]?.data?.count != null && (
                    <span className="text-[10px] text-slate-500 ml-2">
                      {results[activeRoutine].data.count} element{results[activeRoutine].data.count > 1 ? "s" : ""}
                      {results[activeRoutine].data.opps_count != null && ` | ${results[activeRoutine].data.opps_count} opp.`}
                      {results[activeRoutine].data.devis_count != null && ` | ${results[activeRoutine].data.devis_count} devis`}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {results[activeRoutine]?.data?.execution_id && (
                    <button onClick={() => downloadPdf(results[activeRoutine].data.execution_id)}
                      className="flex items-center gap-1.5 text-xs text-[#f7c935] hover:text-[#e6b825] px-3 py-1.5 rounded-lg bg-[#f7c935]/10 hover:bg-[#f7c935]/20 font-bold"
                      data-testid={`export-pdf-${activeRoutine}`}>
                      <Download className="w-3.5 h-3.5" /> Exporter PDF
                    </button>
                  )}
                  <button onClick={() => setActiveRoutine(null)} className="p-1 rounded-lg hover:bg-white/10 text-slate-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-5 max-h-[500px] overflow-y-auto">
                {results[activeRoutine].success ? (
                  <div className="prose-sm">{formatResult(results[activeRoutine].data?.result)}</div>
                ) : (
                  <div className="text-sm text-red-400 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0" /> {results[activeRoutine].error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── History View ─── */}
      {view === "history" && !historyDetail && (
        <div className="p-4">
          {/* Stats bar */}
          {stats && (
            <div className="flex items-center gap-3 mb-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
                {[
                  { label: "Total executions", value: stats.total, color: "#f7c935", icon: BarChart3 },
                  { label: "Cette semaine", value: stats.this_week, color: "#3b82f6", icon: Calendar },
                  { label: "Ce mois", value: stats.this_month, color: "#22c55e", icon: Clock },
                  { label: "Types utilises", value: Object.keys(stats.by_type || {}).filter(k => (stats.by_type[k]?.total || 0) > 0).length + "/3", color: "#8b5cf6", icon: Sparkles },
                ].map(s => (
                  <div key={s.label} className="bg-[#0b0811] border border-white/5 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: s.color + "15" }}>
                      <s.icon className="w-4 h-4" style={{ color: s.color }} />
                    </div>
                    <div>
                      <div className="text-[10px] text-slate-500 uppercase font-bold">{s.label}</div>
                      <div className="text-lg font-black" style={{ color: s.color }}>{s.value}</div>
                    </div>
                  </div>
                ))}
              </div>
              {history.length > 0 && (
                <button onClick={clearAllHistory}
                  className="px-3 py-2 text-xs font-bold rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 whitespace-nowrap flex items-center gap-1.5 self-start"
                  data-testid="clear-all-history">
                  <Trash2 className="w-3.5 h-3.5" /> Tout effacer
                </button>
              )}
            </div>
          )}

          {/* History list */}
          {historyLoading ? (
            <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#f7c935]" /></div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-10 h-10 mx-auto mb-3 text-slate-600" />
              <p className="text-sm text-slate-500">Aucun historique. Lancez une routine pour commencer !</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-white/5">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/[0.06] bg-[#0b0811]">
                    <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Type</th>
                    <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Date</th>
                    <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Execute par</th>
                    <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => {
                    const r = ROUTINE_MAP[h.routine_type];
                    const Icon = r?.icon || Sparkles;
                    const color = r?.color || "#64748b";
                    return (
                      <tr key={h.execution_id} className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => setHistoryDetail(h)} data-testid={`history-item-${h.execution_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: color + "20" }}>
                              <Icon className="w-3.5 h-3.5" style={{ color }} />
                            </div>
                            <span className="text-xs font-bold text-white">{r?.title || h.routine_type}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[11px] text-slate-400">
                          {new Date(h.executed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-400">{h.executed_by || "-"}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button onClick={e => { e.stopPropagation(); downloadPdf(h.execution_id); }}
                              className="inline-flex items-center gap-1 text-[10px] text-[#f7c935] hover:text-[#e6b825] px-2.5 py-1 rounded-lg bg-[#f7c935]/10 hover:bg-[#f7c935]/20 font-bold"
                              data-testid={`history-pdf-${h.execution_id}`}>
                              <Download className="w-3 h-3" /> PDF
                            </button>
                            <button onClick={e => { e.stopPropagation(); deleteExecution(h.execution_id); }}
                              disabled={deleting === h.execution_id}
                              className="inline-flex items-center text-[10px] text-red-400 hover:text-red-300 p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20"
                              data-testid={`history-delete-${h.execution_id}`}>
                              {deleting === h.execution_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── History Detail ─── */}
      {view === "history" && historyDetail && (
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-sm font-bold text-white">{ROUTINE_MAP[historyDetail.routine_type]?.title || historyDetail.routine_type}</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {new Date(historyDetail.executed_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                {historyDetail.executed_by && ` — ${historyDetail.executed_by}`}
              </div>
            </div>
            <button onClick={() => downloadPdf(historyDetail.execution_id)}
              className="flex items-center gap-1.5 text-xs text-[#f7c935] hover:text-[#e6b825] px-4 py-2 rounded-lg bg-[#f7c935]/10 hover:bg-[#f7c935]/20 font-bold"
              data-testid="detail-export-pdf">
              <Download className="w-4 h-4" /> Exporter PDF
            </button>
          </div>
          <div className="bg-[#0b0811] border border-white/5 rounded-xl p-5 max-h-[500px] overflow-y-auto" data-testid="history-detail-content">
            {formatResult(historyDetail.result)}
          </div>
        </div>
      )}
    </div>
  );
}
